import { ethers } from "ethers";
import { useEffect, useRef, useState } from "react";
import { notification } from "antd";
import { message } from "antd";
import { get, post } from "network/axiosClient";
import { LoginResponse } from "components/connectWallet/store/type";
import { useDispatch, useSelector } from "react-redux";
import { setAuthentication, selectWalletAddress } from "components/connectWallet/store/slice";
import { uuid } from "uuidv4";
import moment from "moment";
import { getAuthorizationHeader } from "@/utils/authentication";
import { safeStorage } from "@/utils/localStorage";
import { useAccount } from "wagmi";
import { useEthersSigner } from "@/hooks/useEthersSigner";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useReferral } from "@/hooks/useReferral";
import * as Sentry from "@sentry/react";

const useConnectWallet = () => {
  const dispatch = useDispatch();
  const { getReferralAddress, clearReferralAddress, saveCurrentWalletAddress } = useReferral();

  const walletAddress = useSelector(selectWalletAddress);
  const [isConnectMetamask, setIsConnectMetamask] = useState<boolean>(false);
  const [openSignWallet, setOpenSignWallet] = useState(false);

  const { address, isConnected, connector } = useAccount();
  const { openConnectModal } = useConnectModal();
  const signer = useEthersSigner();

  const currentAccount = useRef<string>("");

  const [, contextHolder] = notification.useNotification();

  useEffect(() => {
    // Initialize any necessary setup
  }, []);

  useEffect(() => {
    const accessTokenStorage = safeStorage.getItem("accessToken");
    if (address && isConnected && !accessTokenStorage) {
      setOpenSignWallet(true);
    }

    if (address && isConnected) {
      saveCurrentWalletAddress(address);
    }
  }, [address, isConnected, saveCurrentWalletAddress]);

  const callLogin = async (
    walletAddress: string,
    signature: string,
    generateUUID: string
  ) => {
    try {
      const referralAddress = getReferralAddress();

      let params = {
        walletAddress,
        deadline: moment().unix() + 5 * 60 * 1000,
        sig: signature,
        uuid: generateUUID,
        referral: referralAddress || "",
      };

      const res: LoginResponse = await post("/auth/sign-in", params);
      if (res && res.data && res.data.success) {
        safeStorage.setItem("accessToken", res?.data?.data?.access_token || "");
        safeStorage.setItem("refreshToken", res?.data?.data?.refresh_token || "");
        safeStorage.setItem("walletAddress", walletAddress);

        dispatch(
          setAuthentication({
            accessToken: res?.data?.data?.access_token,
            refreshToken: res?.data?.data?.refresh_token,
            walletAddress,
          })
        );

        // ── Sentry: ghi nhận user đã login thành công ──────────────────────
        Sentry.setUser({
          username: walletAddress,
          ip_address: "{{auto}}",
        });
        Sentry.captureMessage("wallet.login_success", {
          level: "info",
          tags: {
            section: "login-vi",
            action: "sign_in",
          },
          extra: {
            walletAddress,
            referral: referralAddress || null,
            provider: safeStorage.getItem("provider") ?? "unknown",
          },
        });
        // ───────────────────────────────────────────────────────────────────

        get("/moralis/collection-nfts", {
          headers: getAuthorizationHeader(),
        });

        if (referralAddress) {
          clearReferralAddress();
        }

        message.success("Login Success", 3);
      } else {
        message.error("Login failed: Invalid response", 3);
      }
    } catch (e) {
      Sentry.captureException(e, {
        tags: { section: "login-vi", action: "call_login" },
        extra: { walletAddress },
      });
      message.error(e?.message);
    }
  };

  const signAndAccept = async (callback?: () => void) => {
    if (!isConnected || !address || !signer) {
      openConnectModal();
      message.success("Login Failed", 3);
      callback?.();
      return;
    } else {
      try {
        const generateUUID = uuid();
        const message = `Welcome to Monbase NFT! Click to sign in and accept the Monbase NFT Terms of Service: https://nft.monbase.com. This request will not trigger a blockchain transaction or cost any gas fees. Your authentication status will reset after 24 hours. Wallet address: ${address?.toLowerCase()}. Nonce: ${generateUUID}`;
        const signature = await signer?.signMessage(message);
        const verify = ethers.utils.verifyMessage(message, signature ?? "");
        safeStorage.setItem("provider", connector?.name ?? "");

        if (verify) {
          await callLogin(address ?? "", signature ?? "", generateUUID);
        }
        callback?.();
      } catch (e) {
        Sentry.captureException(e, {
          tags: { section: "login-vi", action: "sign_and_accept" },
          extra: { walletAddress },
        });
        message.error(e?.message, 3);
        callback?.();
      }
    }
  };

  function checkSignConnectWallet() {
    const accessTokenStorage = safeStorage.getItem("accessToken");
    if (walletAddress && !accessTokenStorage) {
      return true;
    }
    Sentry.captureException(new Error("Check sign connect wallet failed"), {
      tags: { section: "login-vi", action: "check_sign_connect_wallet" },
      extra: { walletAddress },
    });
    return false;
  }

  function checkAccessToken() {
    const accessTokenStorage = safeStorage.getItem("accessToken");

    if (accessTokenStorage) {
      return true;
    }
    Sentry.captureException(new Error("Check access token failed"), {
      tags: { section: "login-vi", action: "check_access_token" },
      extra: { walletAddress },
    });
    return false;
  }

  function checkMetamaskProvider() {
    const providerStorage = safeStorage.getItem("provider");
    if (providerStorage === "metamask") {
      return true;
    }
    Sentry.captureException(new Error("Check metamask provider failed"), {
      tags: { section: "login-vi", action: "check_metamask_provider" },
      extra: { walletAddress },
    });
    return false;
  }

  return {
    walletAddress,
    setWalletAddress: () => { },
    currentAccount,
    contextHolder,
    isConnectMetamask,
    checkSignConnectWallet,
    openSignWallet,
    setOpenSignWallet,
    checkMetamaskProvider,
    account: address,
    signAndAccept,
    checkAccessToken,
    setIsConnectMetamask,
  };
};

export default useConnectWallet;
